import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { DocumentAsset } from '../../../services/admin/adminBackendServiceDocumentLibraryMixin';
import DocumentLibraryCard from './DocumentLibraryCard';
import { DeleteDocumentDialog, EditDocumentDialog } from './DocumentLibraryDialogs';
import DocumentUploadZone from './DocumentUploadZone';
import { useDocumentLibrary } from './useDocumentLibrary';
import { LIBRARY_COLUMN_MAX_WIDTH } from './libraryLayout';
import VideoLibrarySearchField from './VideoLibrarySearchField';

interface DocumentLibraryPanelProps {
  fundOpportunityId: string;
  backendService: BackendService;
}

function DocumentGrid({
  loading,
  documents,
  onDelete,
  onEdit,
}: {
  loading: boolean;
  documents: DocumentAsset[];
  onDelete: (doc: DocumentAsset) => void;
  onEdit: (doc: DocumentAsset) => void;
}) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (documents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No documents yet — upload a document above.
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {documents.map((doc) => (
        <DocumentLibraryCard
          key={doc.externalId}
          fluid
          doc={doc}
          onDelete={() => onDelete(doc)}
          onEdit={() => onEdit(doc)}
        />
      ))}
    </Box>
  );
}

export default function DocumentLibraryPanel({
  fundOpportunityId,
  backendService,
}: DocumentLibraryPanelProps) {
  const library = useDocumentLibrary({ fundOpportunityId, backendService });
  const [pendingDelete, setPendingDelete] = useState<DocumentAsset | null>(null);
  const [editing, setEditing] = useState<DocumentAsset | null>(null);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        maxWidth: LIBRARY_COLUMN_MAX_WIDTH,
      }}
    >
      <Typography variant="h6">Document Library</Typography>
      <Typography variant="body2" color="text.secondary">
        Upload a document once, then import pages or regions while editing.
      </Typography>

      <DocumentUploadZone
        uploading={library.uploading}
        uploadError={library.uploadError}
        onDismissError={library.dismissUploadError}
        onUpload={library.upload}
      />

      <VideoLibrarySearchField fullWidth value={library.search} onChange={library.setSearch} />

      {library.error && (
        <Typography variant="caption" color="error.main">
          {library.error}
        </Typography>
      )}

      <DocumentGrid
        loading={library.loading}
        documents={library.documents}
        onDelete={setPendingDelete}
        onEdit={setEditing}
      />

      <EditDocumentDialog
        doc={editing}
        onCancel={() => setEditing(null)}
        onSave={async (externalId, fields) => {
          await library.updateMetadata(externalId, fields);
          setEditing(null);
        }}
      />

      <DeleteDocumentDialog
        doc={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={(force) => {
          const doc = pendingDelete!;
          setPendingDelete(null);
          void library.remove(doc.externalId, force);
        }}
      />
    </Box>
  );
}
